import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PickupsPage from "./PickupsPage";
import DropoffPage from "./DropoffPage";
import {
  CircleCheck,
  Phone,
  Package,
  Truck,
} from "lucide-react";
import useAuthenticationCheck from "./authenticationCheck";


const DeliveryPartnerPage = () => {
  useAuthenticationCheck();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const deliveryGroupId = queryParams.get("deliveryGroupId");

  const [deliveryGroup, setDeliveryGroup] = useState(null);
  const [error, setError] = useState("");

  /**
   * Stage mapping
   * 1 = Pickups
   * 2 = Dropoff
   * 3 = Completed
   */
  const [stage, setStage] = useState(1);

  // --------------------------------------------------
  // Fetch Delivery Group (single source of truth)
  // --------------------------------------------------
  useEffect(() => {
    const fetchDeliveryGroup = async () => {
      try {
        if (!deliveryGroupId) {
          setError("DeliveryGroupId missing");
          return;
        }

        const URL = process.env.REACT_APP_FORE_BRICKBUCKET;

        const res = await fetch(
          `${URL}fulfillment/items/delivery?deliveryGroupId=${deliveryGroupId}`
        );

        if (!res.ok) throw new Error("Failed fetching delivery group");

        const data = await res.json();
        if (!data.success) {
          setError(data.message || "Invalid delivery group");
          return;
        }

        const dg = data.deliveryGroup;
        setDeliveryGroup(dg);

        // 🔑 Backend → UI stage mapping
        const backendStatus = dg?.status?.toLowerCase();
        console.log('backendStatus', backendStatus)
        if (backendStatus === "delivered" ) {
          setStage(3);
        } else if (backendStatus === "out for delivery" || backendStatus === 'items picked up') {
          setStage(2);
        } else {
          setStage(1);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    };

    fetchDeliveryGroup();
  }, [deliveryGroupId]);

  // --------------------------------------------------
  // Events from children
  // --------------------------------------------------

  // Pickups finished → Move to Dropoff
  const handlePickupsCompleted = () => {
    setStage(2);
    setDeliveryGroup((prev) =>
      prev ? { ...prev, status: "Out For Delivery" } : prev
    );
  };

  // Delivery finished → Completed
  const handleDeliveryCompleted = () => {
    setStage(3);
    setDeliveryGroup((prev) =>
      prev ? { ...prev, status: "Delivered" } : prev
    );
  };

  // --------------------------------------------------
  // Error / Loading
  // --------------------------------------------------

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center text-red-600 font-bold p-6">
        {error}
      </div>
    );
  }

  if (!deliveryGroup) {
    return (
      <div className="min-h-screen flex justify-center items-center text-gray-600 font-semibold">
        Loading delivery data...
      </div>
    );
  }

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-200 flex justify-center">
      {/* Mobile Frame */}
      <div className="w-full max-w-[420px] bg-white shadow-2xl overflow-hidden h-screen flex flex-col">

        {/* HEADER (FIXED) */}
        <div className="bg-red-600 text-white p-5 shadow-md flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {stage === 1 && <Package className="w-7 h-7" />}
              {stage === 2 && <Truck className="w-7 h-7" />}
              {stage === 3 && <CircleCheck className="w-7 h-7" />}
              <div>
                <h1 className="font-bold">Delivery Partner</h1>
                <p className="text-xs text-red-100">
                  {stage === 1 && "Pickup"}
                  {stage === 2 && "Delivery"}
                  {stage === 3 && "Completed"}
                </p>
              </div>
            </div>

            <div className="text-xs text-right">
              <div className="font-bold">
                {deliveryGroup.readable_order_number || "—"}
              </div>
              <div className="text-red-100">Order ID</div>
            </div>
          </div>
        </div>

        {/* MIDDLE (SCROLLABLE) */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {stage === 1 && (
            <PickupsPage
              deliveryGroup={deliveryGroup}
              onComplete={handlePickupsCompleted}
            />
          )}

          {stage === 2 && (
            <DropoffPage
              deliveryGroup={deliveryGroup}
              onDelivered={handleDeliveryCompleted}
            />
          )}

          {stage === 3 && (
            <div className="p-5">
              <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center border-4 border-green-300">
                    <CircleCheck className="w-12 h-12 text-green-600" />
                  </div>
                </div>

                <h2 className="font-bold text-green-700 mb-6">
                  Delivery Completed Successfully
                </h2>

                <button
                  onClick={() =>
                    (window.location.href = "tel:+919876543210")
                  }
                  className="w-full mt-6 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 flex items-center justify-center gap-3 shadow-lg"
                >
                  <Phone className="w-5 h-5" />
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER (FIXED) */}
        <div className="bg-white border-t-2 border-gray-200 p-3 grid grid-cols-4 gap-2 flex-shrink-0">
          <NavButton active={stage === 1} icon={<Package />} label="Pickups" />
          <NavButton active={stage === 2} icon={<Truck />} label="Delivery" disabled={stage < 2} />
          <NavButton active={stage === 3} icon={<CircleCheck />} label="Complete" disabled={stage < 3} />
          <NavButton
            icon={<Phone />}
            label="Support"
            onClick={() => (window.location.href = "tel:+919876543210")}
          />
        </div>
      </div>
    </div>
  );
};

const NavButton = ({ icon, label, active, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
      active
        ? "bg-red-50 text-red-600"
        : "text-gray-500 hover:bg-gray-50"
    } ${disabled ? "opacity-40" : ""}`}
  >
    {icon}
    <span className="text-xs font-semibold">{label}</span>
  </button>
);

export default DeliveryPartnerPage;
